import { hasAttributeValue, isAttributeType, toBoolean } from './attribute-value-checks';
import type {
  AttributeTypeErrorFinding,
  CustomPermissionsConflictFinding,
  DuplicateUniqueValueFinding,
  EmployeeImportRow,
  EmployeeToGrantAccess,
  ExistingEmployee,
  ExistingEmployeeUser,
  ImportRow,
  InvitingDisabledEmployeeFinding,
  MissingRequiredFieldFinding,
  ModifiedEmployeeIdFinding,
  UnknownAttributeNameFinding,
  UnknownRoleNameFinding,
  UnsupportedLanguageFinding,
  UserMissingEmailFinding,
  ValidatableAttribute,
  ValidateImportInput,
  ValidateImportResult,
} from './types';

/**
 * These key names are this feature's existing legacy conventions -- see
 * `LEGACY_KEY_FOR_CORE_FIELD`/`withLegacyCoreFieldKeys` in
 * `apps/pulse/client/src/behaviors/actions/employee.actions.ts`, which callers of this package
 * apply to a row before passing it in. They are stable, shared contracts, not configuration.
 */
const COMPANY_EMPLOYEE_ID_KEY = 'employee id';
const LANGUAGE_KEY = 'language';
const ACTIVE_KEY = 'enabled';
const EMAIL_KEY = 'email';
const FIRST_NAME_KEY = 'first name';
const LAST_NAME_KEY = 'last name';
const ROLE_KEY = '$survey_role';
const CUSTOM_PERMISSIONS_STRING = 'CUSTOM_PERMISSIONS';

/**
 * Stands in for a row with no `last name` value at all (e.g. a company that doesn't map a Last
 * Name column) when building the lastName+email match key below, so a row missing that key isn't
 * mistaken for one whose last name is genuinely an empty string.
 */
const NO_LAST_NAME_VALUE = 'undefined';

/**
 * Create a set of `unique` elements from `list` and then filter out all elements from `list`
 * which exist in the `unique` set; whatever remains is a duplicate.
 */
function duplicatesFrom<T>(list: T[]): T[] {
  const unique = new Set(list);
  return list.filter((el) => {
    if (unique.has(el)) {
      unique.delete(el);
      return false;
    }
    return true;
  });
}

function isUserDisabled(user: ExistingEmployeeUser | null | undefined): boolean {
  const userNonDisabledStatuses = ['ACTIVE', 'INVITATION_PENDING'];
  return !userNonDisabledStatuses.includes(user?.status as string);
}

/**
 * A row from the input, normalized the way every check in this module expects: the company
 * employee ID lowercased and trimmed, matching how it's stored on the backend.
 */
function normalizeEmployeeId(employee: EmployeeImportRow): EmployeeImportRow {
  return {
    rowIndex: employee.rowIndex,
    row: {
      ...employee.row,
      [COMPANY_EMPLOYEE_ID_KEY]: (employee.row[COMPANY_EMPLOYEE_ID_KEY] ?? '').toLowerCase().trim(),
    },
  };
}

function buildValidAttributes(employeeAttributes: ValidatableAttribute[]): ValidatableAttribute[] {
  return [
    { name: ACTIVE_KEY, type: 'BOOLEAN', isRequired: true, isUnique: false },
    { name: LANGUAGE_KEY, type: 'STRING', isRequired: false, isUnique: false },
    { name: ROLE_KEY, type: 'STRING', isRequired: false, isUnique: false },
    ...employeeAttributes
      .map((attr) => ({ ...attr, name: attr.name.toLowerCase() }))
      .filter((attr) => !attr.isArchived),
  ];
}

/** Every existing employee for this company, keyed by `lastName + email` -- the same fragile-but-existing match key `modifiedEmployeeId` detection has always used. Employees with no email are skipped, since they can never be matched by this key. */
function buildExistingEmployeesByLastNameEmail(
  existingEmployees: ExistingEmployee[],
): Record<string, ExistingEmployee> {
  return existingEmployees.reduce<Record<string, ExistingEmployee>>((acc, employee) => {
    if (!employee.email) return acc;
    acc[employee.lastName + employee.email] = employee;
    return acc;
  }, {});
}

/** Detects duplicate values across every `isUnique` attribute (this is how duplicate employee IDs and emails both get caught). */
export function findDuplicateUniqueValues(
  validEmployees: EmployeeImportRow[],
  uniqueAttributeNames: string[],
): DuplicateUniqueValueFinding[] {
  const findings: DuplicateUniqueValueFinding[] = [];

  uniqueAttributeNames.forEach((attrName) => {
    const allValues = validEmployees.reduce<string[]>((acc, { row }) => {
      const value = row[attrName]?.toLowerCase();
      if (!value) return acc;
      return [...acc, value];
    }, []);

    duplicatesFrom(allValues).forEach((value) => {
      if (!findings.some((f) => f.attrName === attrName && f.value === value)) {
        findings.push({ type: 'duplicateUniqueValue', attrName, value });
      }
    });
  });

  return findings;
}

export interface RowLevelFindings {
  modifiedEmployeeIdErrors: ModifiedEmployeeIdFinding[];
  missingRequiredErrors: MissingRequiredFieldFinding[];
  unknownAttributeNames: UnknownAttributeNameFinding[];
  attributeTypeErrors: AttributeTypeErrorFinding[];
  unsupportedLangs: UnsupportedLanguageFinding[];
  unknownRoleNamesRows: UnknownRoleNameFinding[];
  customPermissionsRows: CustomPermissionsConflictFinding[];
  invitingDisabledEmployee: InvitingDisabledEmployeeFinding[];
  userMissingEmailRows: UserMissingEmailFinding[];
  /** Users to deactivate because their role was cleared on an otherwise-matched existing employee (a distinct trigger from `findEmployeesToDeactivate`'s override-mode check, which this gets merged with). */
  usersToDeactivate: Map<string, ExistingEmployeeUser>;
}

/**
 * Every check that needs one pass over the imported rows together with each row's matched
 * existing employee (found by `lastName + email`). Kept as a single pass -- matching the
 * original implementation -- so every check below shares the exact same `existingEmployee` lookup
 * rather than risking each deriving it slightly differently.
 */
export function computeRowLevelFindings(
  validEmployees: EmployeeImportRow[],
  existingEmployeesByLastNameEmail: Record<string, ExistingEmployee>,
  validAttributes: ValidatableAttribute[],
  validAttributeNames: string[],
  supportedLanguages: string[],
  validRoleNames: string[],
): RowLevelFindings {
  const result: RowLevelFindings = {
    modifiedEmployeeIdErrors: [],
    missingRequiredErrors: [],
    unknownAttributeNames: [],
    attributeTypeErrors: [],
    unsupportedLangs: [],
    unknownRoleNamesRows: [],
    customPermissionsRows: [],
    invitingDisabledEmployee: [],
    userMissingEmailRows: [],
    usersToDeactivate: new Map(),
  };

  validEmployees.forEach(({ rowIndex, row: employee }) => {
    const employeeEmail = employee[EMAIL_KEY];
    const searchKey = (employee[LAST_NAME_KEY] ?? NO_LAST_NAME_VALUE) + (employee[EMAIL_KEY] ?? '');
    const employeeId = employee[COMPANY_EMPLOYEE_ID_KEY] as string;
    const existingEmployee = employeeEmail ? existingEmployeesByLastNameEmail[searchKey] : undefined;

    if (existingEmployee && employeeId !== existingEmployee.employeeId) {
      result.modifiedEmployeeIdErrors.push({
        type: 'modifiedEmployeeId',
        rowIndex,
        employeeName: `${employee[FIRST_NAME_KEY] ?? ''} ${employee[LAST_NAME_KEY] ?? ''}`,
        oldEmployeeId: existingEmployee.employeeId,
        newEmployeeId: employeeId,
      });
    }

    validAttributes.forEach((attr) => {
      if (attr.isRequired && !hasAttributeValue(employee[attr.name])) {
        result.missingRequiredErrors.push({
          type: 'missingRequiredField',
          rowIndex,
          employeeId,
          attrName: attr.name,
        });
      }
    });

    Object.keys(employee).forEach((attrName) => {
      const attr = validAttributes.find((validAttr) => validAttr.name.toLowerCase() === attrName.toLowerCase());

      if (!validAttributeNames.includes(attrName.toLowerCase())) {
        if (!result.unknownAttributeNames.some((f) => f.attrName === attrName)) {
          result.unknownAttributeNames.push({ type: 'unknownAttributeName', attrName });
        }
        return;
      }

      if (employee[attrName] && !isAttributeType(attr?.type as string, employee[attrName])) {
        result.attributeTypeErrors.push({
          type: 'attributeTypeError',
          rowIndex,
          employeeId,
          attrName,
          attrType: attr?.type as string,
        });
      }

      if (
        attrName === LANGUAGE_KEY &&
        employee[LANGUAGE_KEY] &&
        !supportedLanguages.includes(employee[LANGUAGE_KEY] as string) &&
        !result.unsupportedLangs.some((f) => f.language === employee[LANGUAGE_KEY])
      ) {
        result.unsupportedLangs.push({ type: 'unsupportedLanguage', language: employee[LANGUAGE_KEY] as string });
      }
    });

    const hasRoleColumn = Object.prototype.hasOwnProperty.call(employee, ROLE_KEY);
    const employeeRole = employee[ROLE_KEY]?.toLowerCase();
    const isCustomPermissions = employee[ROLE_KEY] && employee[ROLE_KEY] === CUSTOM_PERMISSIONS_STRING;
    const isRoleValid = validRoleNames.includes(employeeRole as string);

    if (hasRoleColumn && employeeRole && !isRoleValid && !isCustomPermissions) {
      result.unknownRoleNamesRows.push({ type: 'unknownRoleName', rowIndex });
    }

    if (
      hasRoleColumn &&
      isCustomPermissions &&
      (!existingEmployee || !existingEmployee.isUser || existingEmployee?.hasRole)
    ) {
      result.customPermissionsRows.push({ type: 'customPermissionsConflict', rowIndex });
    }

    // Enabled is a raw import value ('yes'/'no' in any casing, per a real customer export), so this
    // coerces it the same way the client's importEmployees thunk already does elsewhere, rather
    // than string-comparing against the literal 'Yes'.
    const newUserInviteForDisabledEmployee =
      hasRoleColumn && employeeRole && !existingEmployee?.hasRole && !toBoolean(employee?.[ACTIVE_KEY]);
    if (newUserInviteForDisabledEmployee) {
      result.invitingDisabledEmployee.push({ type: 'invitingDisabledEmployee', rowIndex });
    }

    // Preserved exactly as today: `existingEmployee.user` (no `?.` after `existingEmployee`) is
    // only ever reached when `hasRoleColumn && !employeeRole`, which in practice only occurs for a
    // row matched to an existing employee -- see `apps/pulse/client/src/behaviors/actions/employee.actions.ts`'s
    // original implementation for the same shape.
    if (
      hasRoleColumn &&
      !employeeRole &&
      !isUserDisabled(existingEmployee?.user) &&
      !existingEmployee!.user?.worktangoAdmin &&
      existingEmployee?.user?.status?.toLowerCase() === 'active'
    ) {
      result.usersToDeactivate.set(existingEmployee!.user!.email, existingEmployee!.user!);
    }

    const hasEmail = !!employee[EMAIL_KEY];
    const hasRole = !!employee[ROLE_KEY];
    if (!hasEmail && hasRole) {
      result.userMissingEmailRows.push({ type: 'userMissingEmail', rowIndex });
    }
  });

  return result;
}

/** Employees and users to deactivate because they're missing from an `override`-mode import file. No-op for any other import type. */
export function findEmployeesToDeactivate(
  existingEmployees: ExistingEmployee[],
  employeesDict: Record<string, ImportRow>,
  importType: string | undefined,
): { employeesToDeactivate: Map<string, ExistingEmployee>; usersToDeactivate: Map<string, ExistingEmployeeUser> } {
  const employeesToDeactivate = new Map<string, ExistingEmployee>();
  const usersToDeactivate = new Map<string, ExistingEmployeeUser>();

  if (importType !== 'override') {
    return { employeesToDeactivate, usersToDeactivate };
  }

  existingEmployees.forEach((emp) => {
    const employeeIsOnList = !!employeesDict[emp.employeeId];
    if (employeeIsOnList) return;

    if (emp.enabled) {
      employeesToDeactivate.set(emp.employeeId, emp);
    }
    if (!isUserDisabled(emp?.user) && !emp.user?.worktangoAdmin && emp.user?.status?.toLowerCase() === 'active') {
      usersToDeactivate.set(emp.user!.email, emp.user!);
    }
  });

  return { employeesToDeactivate, usersToDeactivate };
}

/** New or newly-role-holding employees who will need a user account created/activated. */
export function findEmployeesToGrantAccess(
  validEmployees: EmployeeImportRow[],
  existingEmployees: ExistingEmployee[],
): EmployeeToGrantAccess[] {
  const employeesToGrantAccess = new Map<string, EmployeeToGrantAccess>();

  validEmployees.forEach(({ row: importedEmployee }) => {
    const importedEmployeeId = importedEmployee[COMPANY_EMPLOYEE_ID_KEY] as string;
    const importedEmployeeRole = importedEmployee[ROLE_KEY];
    const firstName = importedEmployee[FIRST_NAME_KEY];
    const lastName = importedEmployee[LAST_NAME_KEY];
    const existingEmployee = existingEmployees.find((existing) => existing.employeeId === importedEmployeeId);

    const entry = {
      role: importedEmployeeRole as string,
      fullName: `${firstName} ${lastName}`,
      employeeId: importedEmployeeId,
    };

    if (!existingEmployee && !!importedEmployeeRole) {
      employeesToGrantAccess.set(importedEmployeeId, entry);
    }

    if (existingEmployee && !existingEmployee.isUser && !existingEmployee.hasRole && !!importedEmployeeRole) {
      employeesToGrantAccess.set(importedEmployeeId, entry);
    }
  });

  return Array.from(employeesToGrantAccess.values());
}

/**
 * Runs every employee-import validation rule against a set of already-mapped/transformed rows,
 * returning structured findings and warnings rather than pre-localized strings -- callers (the
 * client's live upload modal, or a future server-side email notification) each render their own
 * presentation from this same shared result.
 */
export function validateImport(input: ValidateImportInput): ValidateImportResult {
  const {
    employees,
    employeeAttributes,
    supportedLanguages,
    existingEmployees,
    validRoleNames,
    additionalRecognizedAttributeNames = [],
    importType,
  } = input;

  const validEmployees = employees.map(normalizeEmployeeId);
  const validAttributes = buildValidAttributes(employeeAttributes);
  const validAttributeNames = [...validAttributes.map((attr) => attr.name), ...additionalRecognizedAttributeNames];
  const uniqueAttributeNames = validAttributes.reduce<string[]>(
    (acc, attr) => (attr.isUnique ? [...acc, attr.name] : acc),
    [],
  );

  const employeesDict = validEmployees.reduce<Record<string, ImportRow>>((acc, { row }) => {
    acc[row[COMPANY_EMPLOYEE_ID_KEY] as string] = row;
    return acc;
  }, {});
  const existingEmployeesByLastNameEmail = buildExistingEmployeesByLastNameEmail(existingEmployees);

  const duplicateUniqueValues = findDuplicateUniqueValues(validEmployees, uniqueAttributeNames);
  const rowLevel = computeRowLevelFindings(
    validEmployees,
    existingEmployeesByLastNameEmail,
    validAttributes,
    validAttributeNames,
    supportedLanguages,
    validRoleNames,
  );
  const { employeesToDeactivate, usersToDeactivate: overrideUsersToDeactivate } = findEmployeesToDeactivate(
    existingEmployees,
    employeesDict,
    importType,
  );
  const grantAccess = findEmployeesToGrantAccess(validEmployees, existingEmployees);

  const usersToDeactivate = new Map<string, ExistingEmployeeUser>([
    ...rowLevel.usersToDeactivate,
    ...overrideUsersToDeactivate,
  ]);

  const findings = {
    duplicateUniqueValues,
    unknownAttributeNames: rowLevel.unknownAttributeNames,
    missingRequiredErrors: rowLevel.missingRequiredErrors,
    attributeTypeErrors: rowLevel.attributeTypeErrors,
    modifiedEmployeeIdErrors: rowLevel.modifiedEmployeeIdErrors,
    unsupportedLangs: rowLevel.unsupportedLangs,
    unknownRoleNamesRows: rowLevel.unknownRoleNamesRows,
    customPermissionsRows: rowLevel.customPermissionsRows,
    userMissingEmailRows: rowLevel.userMissingEmailRows,
    invitingDisabledEmployee: rowLevel.invitingDisabledEmployee,
  };

  const isValid = Object.values(findings).every((values) => values.length === 0);

  return {
    isValid,
    findings,
    warnings: {
      deactivateUsers: Array.from(usersToDeactivate.values()),
      deactivateEmployees: Array.from(employeesToDeactivate.values()),
      grantAccess,
    },
  };
}
